import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ChevronDownIcon, CrossIcon } from '../../../assets';
import {
  CampaignDataProps,
  ConvertOnTheSpotViewProps,
} from './startCampaignModels';
import {
  campaignMessageTypesConfig,
  campaignViewValues,
} from '../../../config/CampaignsConfig';
import { useAppData } from '../../../context/AppContext';
import {
  generateMessageByCampaignById,
  updateCampaign,
} from '../../../core/requests/requests';
import { InfoModal } from '../../../components';
import useOutsideClick from '../../../hooks/useOutsideClicks';
import SelectPhoneNumbersModal from './SelectPhoneNumbersModal';
import CampaignStartedModal from './CampaignStartedModal';

const ConvertOnTheSpotView: React.FC<ConvertOnTheSpotViewProps> = ({
  setCurrentMessageType,
}) => {
  const {
    setCurrentStartCampaignView,
    currentCampaign,
    setCurrentCampaign,
    setShowModalBackground,
  } = useAppData();
  const navigate = useNavigate();

  const [message, setMessage] = useState<string>('');
  const [generatedMessage, setGeneratedMessage] = useState<string>('');
  const [showInfoModal, setShowInfoModal] = useState<string>('');
  const [showSuccessModal, setShowSuccessModal] = useState<string>('');
  const [isAiCheckboxChecked, setIsAiCheckboxChecked] = useState<boolean>(true);
  const [isAiIncluded, setIsAiIncluded] = useState<boolean>(true);
  const [generatingMessage, setGeneratingMessage] = useState<boolean>(false);
  const [showSelectPhoneNumberModal, setShowSelectPhoneNumberModal] =
    useState<boolean>(false);
  const [aiTalkLimit, setAiTalkLimit] = useState<number>(30);
  const [aiProvider, setAiProvider] = useState<string>('OPENAI');
  const [isProviderDropdownOpen, setIsProviderDropdownOpen] = useState(false);

  const providerDropdownRef = React.useRef<HTMLDivElement>(null);
  const providerExcludeClickRef = React.useRef<HTMLDivElement>(null);

  useOutsideClick(
    providerDropdownRef,
    () => setIsProviderDropdownOpen(false),
    providerExcludeClickRef
  );

  const currentCampaignId = currentCampaign?.id;
  const messageType = campaignMessageTypesConfig?.convertOnTheSpotType;

  React.useEffect(() => {
    if (currentCampaign) {
      if (currentCampaign.aiTalkLimit) {
        setAiTalkLimit(currentCampaign.aiTalkLimit);
      }
      if (currentCampaign.aiProvider) {
        setAiProvider(currentCampaign.aiProvider);
      }
    }
  }, [currentCampaign]);

  const handleCloseInfoModal = () => {
    setShowInfoModal('');
    setShowModalBackground(false);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal('');
    setShowModalBackground(false);
    navigate('/campaigns');
  };

  const handleOpenInfoModal = () => {
    setShowInfoModal('Please Enter an Initial Message or Enable AI Assistance');
    setShowModalBackground(true);
  };

  const handleCheckGenerateMessage = () => {
    setIsAiCheckboxChecked(!isAiCheckboxChecked);
    setMessage('');
  };

  const handleCheckIncludeAi = () => {
    setIsAiIncluded(!isAiIncluded);
    setMessage('');
  };

  const handleChangeView = () => {
    setCurrentMessageType(messageType);
    setCurrentStartCampaignView(campaignViewValues?.chooseMedia);
  };

  const handleClearMessage = () => {
    setMessage('');
  };

  const handleShowSelectPhoneNumberModal = () => {
    setShowModalBackground(true);
    setShowSelectPhoneNumberModal(true);
  };

  const handleCloseSelectPhoneNumberModal = () => {
    setShowSelectPhoneNumberModal(false);
    setShowModalBackground(false);
  };

  const handleSendMessage = async () => {
    try {
      if (currentCampaignId) {
        const campaignPayload: CampaignDataProps = {
          ...currentCampaign,
          initialMessage: message ? message : null,
          type: 'CONVERT_ON_THE_SPOT',
          aiTalkLimit: aiTalkLimit,
          aiProvider: aiProvider,
        };
        const { data } = await updateCampaign(campaignPayload);
        if (data) {
          setCurrentCampaign(data);
          const isSmsPlatform = Array.isArray(data.platforms) && (data.platforms as string[]).includes('SMS');

          if (isSmsPlatform && !data.phoneId) {
            handleShowSelectPhoneNumberModal();
          } else {
            setShowModalBackground(true);
            setShowSuccessModal('Your campaign has been successfully created!');
            setTimeout(() => {
              handleCloseInfoModal();
              setCurrentStartCampaignView(campaignViewValues?.startCampaign);
              navigate('/campaigns');
            }, 2000);
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSendInitialMessage = () => {
    // Check if message is empty and "Generate initial message via AI" checkbox is unchecked
    if (!message && !isAiCheckboxChecked) {
      handleOpenInfoModal(); // Open modal if conditions are met
    } else {
      handleSendMessage(); // Proceed with the original logic
      //handleChangeView();
      handleClearMessage();
    }
  };

  const handleGenerateInitialMessage = async () => {
    try {
      if (currentCampaignId && isAiCheckboxChecked) {
        setGeneratingMessage(true);
        const { data } = await generateMessageByCampaignById(
          currentCampaignId,
          aiProvider,
          aiTalkLimit
        );
        if (data) {
          setGeneratedMessage(data.toString());
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setGeneratingMessage(false);
    }
  };

  return (
    <>
      <div className="d-flex-start-start-row pt-1_75rem w-100 mb-1_5rem">
        <div className="d-flex-start-start w-65">
          <span className="text-inter-medium pb-0_5rem">
            Optional Initial Message
          </span>
          <div className="d-flex-row w-100">
            <div className="d-flex-row mr-0_625rem w-100 p-relative">
              <input
                placeholder="Input text"
                className="campaign-convert-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isAiCheckboxChecked}
              />
              {message && (
                <div
                  className="d-flex icon-hovered p-absolute right-1rem"
                  onClick={handleClearMessage}
                >
                  <CrossIcon />
                </div>
              )}
            </div>
            {/*<button className="btn-convert-secondary mr-0_625rem">
              + Add Dialog
            </button>*/}
            <button
              className="btn-convert-primary mr-1_5rem"
              onClick={handleSendInitialMessage}
            >
              Start
            </button>
          </div>

          {/* AI Checkboxes */}
          <div className="d-flex-row mt-1rem">
            <input
              type="checkbox"
              className="checkbox-input mr-0_625rem"
              checked={isAiIncluded}
              onChange={handleCheckIncludeAi}
            />
            <span className="text-inter-medium">Include AI for follow up</span>
          </div>
          <div className="d-flex-row mt-0_2rem">
            <input
              type="checkbox"
              className="checkbox-input mr-0_625rem"
              checked={isAiCheckboxChecked}
              onChange={handleCheckGenerateMessage} // Toggle checkbox state
            />
            <span className="text-inter-medium">
              Generate initial message via AI
            </span>
          </div>
        </div>

        {/* Message Preview Section */}
        <div className="d-flex-column w-35 pb-1_75rem pl-1_75rem pr-1_75rem bg-header h-100 b-radius-0_5rem">
          <div className="d-flex-column w-100 mt-1_75rem">
            <div className="d-flex-row w-100 mb-1rem">
              <div className="d-flex-column w-50 mr-0_5rem p-relative">
                <span className="text-inter-medium pb-0_25rem fs-0_75rem">AI Provider</span>
                <div
                  className="d-flex-row campaign-input w-100 pointer"
                  onClick={() => setIsProviderDropdownOpen(!isProviderDropdownOpen)}
                  ref={providerExcludeClickRef}
                  style={{ height: '2.625rem', alignItems: 'center' }}
                >
                  <span className="text-inter-regular fs-0_875rem">{aiProvider === 'OPENAI' ? 'OpenAI' : 'Claude'}</span>
                  <div className="d-flex-center p-absolute right-0_625rem">
                    <ChevronDownIcon
                      className={`${isProviderDropdownOpen && 't-rotate-180'}`}
                    />
                  </div>
                </div>
                {isProviderDropdownOpen && (
                  <div
                    className="p-absolute z1 w-100 overflow-auto max-h-15rem top-3rem bg-white shadow-box-12 b-radius-0_5rem"
                    ref={providerDropdownRef}
                  >
                    <div
                      className={`${aiProvider === 'OPENAI' && 'bg-badge'} d-flex-center-start h-2_375rem bb-1 hovered pointer`}
                      onClick={() => {
                        setAiProvider('OPENAI');
                        setIsProviderDropdownOpen(false);
                      }}
                    >
                      <span className="pl-0_5rem f-roboto-m-primary fs-0_875rem">OpenAI</span>
                    </div>
                    <div
                      className={`${aiProvider === 'CLAUDE' && 'bg-badge'} d-flex-center-start h-2_375rem bb-1 hovered pointer`}
                      onClick={() => {
                        setAiProvider('CLAUDE');
                        setIsProviderDropdownOpen(false);
                      }}
                    >
                      <span className="pl-0_5rem f-roboto-m-primary fs-0_875rem">Claude</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="d-flex-column w-50">
                <span className="text-inter-medium pb-0_25rem fs-0_75rem whitespace-nowrap">AI Reply Limit</span>
                <input
                  type="number"
                  className="campaign-input w-100"
                  value={aiTalkLimit || ''}
                  onChange={(e) => setAiTalkLimit(e.target.value ? parseInt(e.target.value) : 0)}
                  style={{ height: '2.625rem' }}
                />
              </div>
            </div>
          </div>
          <div className="d-flex-start-start b-radius-0_5rem p-2_625rem bg-white w-100 b-1">
            {isAiCheckboxChecked ? (
              <>
                {generatedMessage?.length > 0 && !generatingMessage ? (
                  <div className="d-flex-start-start w-100">
                    <span>{generatedMessage}</span>
                    <div className="d-flex-center w-100">
                      <div
                        className="btn-convert-primary mt-1rem"
                        onClick={handleGenerateInitialMessage}
                      >
                        <span>Refresh</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {generatingMessage ? (
                      <div className="btn-text-preview-primary">
                        <div className="loader"></div>
                      </div>
                    ) : (
                      <div
                        className="btn-text-preview-primary"
                        onClick={handleGenerateInitialMessage}
                      >
                        <span>Preview message</span>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {message?.length > 0 ? (
                  <span>{message}</span>
                ) : (
                  <div className="btn-text-preview-primary">Preview</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {showSelectPhoneNumberModal && (
        <SelectPhoneNumbersModal
          closeModal={handleCloseSelectPhoneNumberModal}
        />
      )}

      {/* Info Modal */}
      {showInfoModal && (
        <InfoModal title={showInfoModal} onClose={handleCloseInfoModal} />
      )}
      {showSuccessModal && (
        <CampaignStartedModal
          title={showSuccessModal}
          onClose={handleCloseSuccessModal}
        />
      )}
    </>
  );
};

export default ConvertOnTheSpotView;
